import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import CloseIcon from "@mui/icons-material/Close";

import { Organisation } from "api/Accounts/AccountsInterfaces";

interface OrganisationLogoUploadProps {
  organisation: Organisation;
  onUpload: (file: File) => Promise<void>;
  onRemove: () => Promise<void>;
}

const OrganisationLogoUpload: React.FC<
  OrganisationLogoUploadProps
> = ({
  organisation,
  onUpload,
  onRemove,
}) => {
  const [logoUpdating, setLogoUpdating] = useState(false);
  const [localObjectUrl, setLocalObjectUrl] = useState<string | null>(
    null
  );

  const logoInputRef = useRef<HTMLInputElement>(null);

  /*
   * Create an object URL only when the logo is a File rather than
   * a URL returned by the backend.
   */
  useEffect(() => {
    if (
      organisation.logo &&
      typeof organisation.logo !== "string"
    ) {
      const objectUrl = URL.createObjectURL(
        organisation.logo
      );

      setLocalObjectUrl(objectUrl);

      return () => {
        URL.revokeObjectURL(objectUrl);
      };
    }

    setLocalObjectUrl(null);
  }, [organisation.logo]);

  const openLogoFilePicker = () => {
    if (logoUpdating) return;

    /*
     * Clearing the input allows the user to select the same file
     * again after it has already been uploaded.
     */
    if (logoInputRef.current) {
      logoInputRef.current.value = "";
    }

    logoInputRef.current?.click();
  };

  const handleLogoUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];

    if (!file || logoUpdating) {
      return;
    }

    setLogoUpdating(true);

    try {
      await onUpload(file);
    } catch (error) {
      console.error("Logo upload failed:", error);
    } finally {
      setLogoUpdating(false);
      event.target.value = "";
    }
  };

  const handleLogoRemove = async (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
    event.stopPropagation();

    if (logoUpdating) {
      return;
    }

    setLogoUpdating(true);

    try {
      await onRemove();
    } catch (error) {
      console.error("Logo removal failed:", error);
    } finally {
      setLogoUpdating(false);
    }
  };

  const logoSource =
    typeof organisation.logo === "string"
      ? organisation.logo
      : localObjectUrl;

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
      }}
    >
      <input
        ref={logoInputRef}
        type="file"
        accept="image/*"
        hidden
        onChange={handleLogoUpload}
      />

      {organisation.logo && logoSource ? (
        <Box
          sx={{
            position: "relative",
            display: "inline-flex",
            maxWidth: {
              xs: 220,
              sm: 280,
            },
            maxHeight: 200,
          }}
        >
          <Tooltip title="Upload a different logo">
            <Box
              component="button"
              type="button"
              onClick={openLogoFilePicker}
              disabled={logoUpdating}
              aria-label="Upload a different organisation logo"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: "auto",
                height: "auto",
                maxWidth: "100%",
                maxHeight: 200,
                p: 0,
                overflow: "hidden",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
                backgroundColor: "background.paper",
                cursor: logoUpdating
                  ? "default"
                  : "pointer",
                opacity: logoUpdating ? 0.65 : 1,
                transition: (theme) =>
                  theme.transitions.create(
                    [
                      "box-shadow",
                      "border-color",
                      "opacity",
                    ],
                    {
                      duration:
                        theme.transitions.duration.short,
                    }
                  ),
                "&:hover": {
                  borderColor: logoUpdating
                    ? "divider"
                    : "primary.main",
                  boxShadow: logoUpdating ? 0 : 3,
                },
                "&:focus-visible": {
                  outline: "3px solid",
                  outlineColor: "primary.light",
                  outlineOffset: 3,
                },
              }}
            >
              <Box
                component="img"
                src={logoSource}
                alt={`${organisation.name} logo`}
                sx={{
                  display: "block",
                  width: "auto",
                  height: "auto",
                  maxWidth: 220,
                  maxHeight: 200,
                  objectFit: "contain",
                }}
              />

              {logoUpdating && (
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor:
                      "rgba(255, 255, 255, 0.7)",
                  }}
                >
                  <CircularProgress size={30} />
                </Box>
              )}
            </Box>
          </Tooltip>

          <Tooltip title="Remove logo">
            <span>
              <IconButton
                aria-label="Remove organisation logo"
                disabled={logoUpdating}
                onClick={handleLogoRemove}
                size="small"
                sx={{
                  position: "absolute",
                  top: -10,
                  right: -10,
                  zIndex: 2,
                  width: 30,
                  height: 30,
                  boxShadow: 2,
                }}
              >
                <CloseIcon
                  sx={{
                    color: "gray",
                    "&:hover": {
                      color: "red",
                    },
                  }}
                />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      ) : (
        <Tooltip title="Upload organisation logo">
          <Box
            component="button"
            type="button"
            onClick={openLogoFilePicker}
            disabled={logoUpdating}
            aria-label="Upload organisation logo"
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minWidth: 170,
              minHeight: 135,
              px: 3,
              py: 2.5,
              color: "text.secondary",
              border: "1px dashed",
              borderColor: "divider",
              borderRadius: 2,
              backgroundColor: "background.paper",
              cursor: logoUpdating
                ? "default"
                : "pointer",
              transition: (theme) =>
                theme.transitions.create(
                  [
                    "color",
                    "background-color",
                    "border-color",
                    "box-shadow",
                  ],
                  {
                    duration:
                      theme.transitions.duration.short,
                  }
                ),
              "&:hover": {
                color: logoUpdating
                  ? "text.secondary"
                  : "primary.main",
                borderColor: logoUpdating
                  ? "divider"
                  : "primary.main",
                backgroundColor: logoUpdating
                  ? "background.paper"
                  : "action.hover",
                boxShadow: logoUpdating ? 0 : 2,
              },
              "&:focus-visible": {
                outline: "3px solid",
                outlineColor: "primary.light",
                outlineOffset: 3,
              },
            }}
          >
            {logoUpdating ? (
              <CircularProgress size={40} />
            ) : (
              <>
                <UploadFileIcon
                  sx={{
                    mb: 1,
                    fontSize: 46,
                  }}
                />

                <Typography
                  component="span"
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                  }}
                >
                  Organisation Logo
                </Typography>
              </>
            )}
          </Box>
        </Tooltip>
      )}
    </Box>
  );
};

export default OrganisationLogoUpload;